import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";

interface DoulaSignupData {
	fullName: string;
	email: string;
	password: string;
	passwordConfirmation: string;
	location: string;
}

const DoulaSignup: React.FC = () => {
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [formData, setFormData] = useState<DoulaSignupData>({
		fullName: "",
		email: "",
		password: "",
		passwordConfirmation: "",
		location: "",
	});

	const locationInputRef = useRef<HTMLInputElement>(null);
	const { place: locationPlace } = usePlacesAutocomplete({
		inputRef: locationInputRef,
		types: ["geocode"],
		componentRestrictions: { country: "us" },
		fields: ["formatted_address", "geometry", "name"],
	});

	useEffect(() => {
		if (!locationPlace?.formatted_address) return;
		setFormData((prev) => ({ ...prev, location: locationPlace.formatted_address ?? "" }));
	}, [locationPlace]);

	const handleInputChange = (field: keyof DoulaSignupData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		setError(null); // Clear errors when user starts typing
	};

	const validateForm = (): boolean => {
		if (!formData.fullName.trim()) {
			setError("Full name is required");
			return false;
		}

		if (!formData.email.trim()) {
			setError("Email is required");
			return false;
		}

		if (!formData.email.includes("@")) {
			setError("Please enter a valid email address");
			return false;
		}

		if (formData.password.length < 6) {
			setError("Password must be at least 6 characters long");
			return false;
		}

		if (formData.password !== formData.passwordConfirmation) {
			setError("Passwords do not match");
			return false;
		}

		if (!formData.location.trim()) {
			setError("Location is required");
			return false;
		}

		return true;
	};

	const handleSignup = async () => {
		if (!validateForm()) return;

		setLoading(true);
		setError(null);

		try {
			// Log the request data
			const requestBody = {
				email: formData.email,
				password: formData.password,
				fullName: formData.fullName,
				role: "doula",
				location: formData.location,
			};

			// Call edge function to create user without email confirmation
			const { data, error } = await supabase.functions.invoke(
				"create-user-no-confirmation",
				{
					body: requestBody,
				},
			);

			if (error) {
				console.error("❌ Edge function error:", error);
				throw error;
			}

			if (data?.data?.user) {
				setSuccess(
					"Account created successfully! Redirecting to onboarding...",
				);

				// Try to sign in immediately
				const { error: signInError } = await supabase.auth.signInWithPassword({
					email: formData.email,
					password: formData.password,
				});

				if (signInError) {
					setError(
						"Account created but sign-in failed. Please try signing in manually.",
					);
					return;
				}

				// Navigate to onboarding after a short delay
				setTimeout(() => {
					navigate("/doula-onboarding");
				}, 2000);
			} else {
				throw new Error("User creation failed");
			}
		} catch (err: any) {
			setError(err.message || "Failed to create account");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 flex items-center justify-center py-8 px-4">
			<div className="w-full max-w-6xl">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Form */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						className="flex justify-center lg:justify-start"
					>
						<Card className="glass-card border-0 shadow-xl w-full max-w-md">
							<CardHeader className="text-center pb-4">
								<CardTitle className="font-normal text-black font-abc-prophet text-4xl leading-tight lg:text-[32px]">
									Become a Doula
								</CardTitle>
								<p className="text-black font-karla leading-relaxed lg:text-xl lg:leading-8">
									Create your account to start helping families
								</p>
							</CardHeader>

							<CardContent className="space-y-4">
								{error && (
									<Alert variant="destructive">
										<AlertDescription>{error}</AlertDescription>
									</Alert>
								)}

								{success && (
									<Alert>
										<AlertDescription>{success}</AlertDescription>
									</Alert>
								)}

								<div className="space-y-3">
									{/* Full Name */}
									<div>
										<Label
											htmlFor="fullName"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Full Name
										</Label>
										<Input
											id="fullName"
											type="text"
											placeholder="Enter your full name"
											value={formData.fullName}
											onChange={(e) =>
												handleInputChange("fullName", e.target.value)
											}
											className="mt-1 pl-3 pr-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
										/>
									</div>

									{/* Email */}
									<div>
										<Label
											htmlFor="email"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Email Address
										</Label>
										<Input
											id="email"
											type="email"
											placeholder="Enter your email"
											value={formData.email}
											onChange={(e) =>
												handleInputChange("email", e.target.value)
											}
											className="pl-3 pr-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md mt-1"
										/>
									</div>

									{/* Location */}
									<div>
										<Label
											htmlFor="location"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Your Location
										</Label>
										<div className="relative mt-1">
											<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
											<Input
												ref={locationInputRef}
												id="location-input"
												type="text"
												placeholder="City, State"
												value={formData.location}
												onChange={(e) =>
													handleInputChange("location", e.target.value)
												}
												className="pl-10 pr-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
											/>
										</div>
									</div>

									{/* Password */}
									<div>
										<Label
											htmlFor="password"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Password
										</Label>
										<div className="relative mt-1">
											<Input
												id="password"
												type={showPassword ? "text" : "password"}
												placeholder="Create a password"
												value={formData.password}
												onChange={(e) =>
													handleInputChange("password", e.target.value)
												}
												className="pl-3 pr-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
											/>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? (
													<EyeOff className="h-4 w-4 text-gray-400" />
												) : (
													<Eye className="h-4 w-4 text-gray-400" />
												)}
											</Button>
										</div>
									</div>

									{/* Confirm Password */}
									<div>
										<Label
											htmlFor="passwordConfirmation"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Confirm Password
										</Label>
										<div className="relative mt-1">
											<Input
												id="passwordConfirmation"
												type={showConfirmPassword ? "text" : "password"}
												placeholder="Confirm your password"
												value={formData.passwordConfirmation}
												onChange={(e) =>
													handleInputChange(
														"passwordConfirmation",
														e.target.value,
													)
												}
												className="pl-3 pr-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
											/>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
												onClick={() =>
													setShowConfirmPassword(!showConfirmPassword)
												}
											>
												{showConfirmPassword ? (
													<EyeOff className="h-4 w-4 text-gray-400" />
												) : (
													<Eye className="h-4 w-4 text-gray-400" />
												)}
											</Button>
										</div>
									</div>
								</div>

								<Button
									onClick={handleSignup}
									disabled={loading}
									className="w-full text-black py-[14px] px-[20px] shadow-btn-shadow font-karla text-[20px] h-auto mt-4 bg-newTheme-lime"
								>
									{loading ? (
										<div className="flex items-center">
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
											Creating Account...
										</div>
									) : (
										"Create Account & Start Onboarding"
									)}
								</Button>

								<div className="text-center">
									<p className="text-sm text-black font-karla">
										Already have an account?{" "}
										<Link
											to="/login"
											className="text-black hover:text-primary-700 font-semibold"
										>
											Sign in
										</Link>
									</p>
								</div>
							</CardContent>
						</Card>
					</motion.div>

					{/* Right Column - Image */}
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						className="flex justify-center lg:justify-end"
					>
						<div className="relative">
							<img
								src="/Gaia_Benefits.png"
								alt="Doula supporting family"
								className="w-full h-auto max-w-lg object-cover aspect-[4/5]"
							/>
						</div>
					</motion.div>
				</div>
			</div>
		</div>
	);
};

export default DoulaSignup;
